import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, from, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { CipherService } from './cipher.service';
import { ResetPasswordFlowContext } from './reset-password-flow.context';

const STORAGE_PREFIX_TV = 'certitoolResetPassword_';
const STORAGE_PREFIX_CCSW5 = 'certitoolResetPasswordCcsw5_';

export interface VerificarIdentidadRequest {
  tipoIdentificacionId: number;
  numeroIdentificacion: string;
}

export interface VerificarIdentidadData {
  celular?: string;
  correoElectronico?: string;
  nombres?: string;
  apellidos?: string;
  tipoIdentificacion?: string;
  numeroIdentificacion?: string;
}

export interface EnviarOtpRequest {
  email: string;
  razonSocial: string;
}

/** Body P10: email/razonSocial (para toLowerCase en backend) + lista (validación). */
export interface EnviarOtpP10Request {
  email: string;
  razonSocial: string;
  lista: Array<{ valor: string }>;
}

export interface ValidarOtpRequest {
  transactionId: string;
  codeOtp: string;
}

export interface InformacionUsuarioRequest {
  tipoIdentificacionId: number;
  numeroIdentificacion: string;
}

export interface InformacionUsuarioData {
  ordenPedidoId?: number | string;
  solicitudTitularId?: number | string;
  cantidad?: number;
  intentos?: number;
  certificados?: Array<{
    cn?: string;
    certPolicy?: string;
    serialNumber?: string;
    certStatus?: string;
    /** Token virtual clásico */
    approvalConceptDate?: string;
    validity?: number;
    /** CCSW5 / código de verificación */
    fechaValidez?: string;
    tipo?: string;
    unidadOrganizacional?: string;
    nombre?: string;
    apellido?: string;
    username?: string;
    titulo?: string;
    solicitudTitularId?: number | string;
    ordenPedidoId?: number | string;
    estado?: string;
    success?: number | string;
  }>;
}

export interface CambiarContrasenaRequest {
  tipoIdentificacionId: number;
  numeroIdentificacion: string;
  passwordNuevo: string;
  ordenPedidoId: number;
  solicitudTitularId: number;
}

/**
 * Reset password token virtual / CCSW5 (código de verificación).
 * Contrato: body cifrado `{ data: "<AES-GCM base64>" }` (misma clave `environment.cipher`).
 *
 * Paths:
 * - Token virtual: /password/verificar-identidad | enviar-otp | validar-otp | informacion-usuario | reset-password
 * - CCSW5:        /password/ccsw5/verificar-identidad | enviar-otp | validar-otp | ccsw5/informacion-usuario | ccsw5/reset-password
 */
@Injectable({ providedIn: 'root' })
export class ResetPasswordService {
  private readonly base = `${environment.certificadoServiceUrl}/password`;

  constructor(
    private http: HttpClient,
    private cipher: CipherService,
    private flowContext: ResetPasswordFlowContext
  ) {}

  verificarIdentidad(body: VerificarIdentidadRequest): Observable<{ data?: VerificarIdentidadData }> {
    return this.postEncrypted<{ data?: VerificarIdentidadData }>(this.pathVerificarIdentidad(), body);
  }

  enviarOtp(body: EnviarOtpRequest): Observable<{ successful?: boolean; transactionId?: string }> {
    return this.postEncrypted<{ successful?: boolean; transactionId?: string }>(
      `${this.base}/enviar-otp`,
      body
    );
  }

  /**
   * Envía OTP para P10: incluye email y razonSocial (evita null en backend) y lista (validación).
   */
  enviarOtpP10(body: EnviarOtpP10Request): Observable<{ successful?: boolean; transactionId?: string }> {
    return this.postEncrypted<{ successful?: boolean; transactionId?: string }>(
      `${this.base}/enviar-otp`,
      body
    );
  }

  validarOtp(body: ValidarOtpRequest): Observable<{ successful?: boolean; data?: { successful?: boolean } }> {
    return this.postEncrypted<{ successful?: boolean; data?: { successful?: boolean } }>(
      `${this.base}/validar-otp`,
      body
    );
  }

  informacionUsuario(body: InformacionUsuarioRequest): Observable<{ data?: InformacionUsuarioData }> {
    return this.postEncrypted<{ data?: InformacionUsuarioData }>(this.pathInformacionUsuario(), body);
  }

  cambiarContrasena(body: CambiarContrasenaRequest): Observable<{ data?: { exitoso?: boolean; mensaje?: string } }> {
    return this.postEncrypted<{ data?: { exitoso?: boolean; mensaje?: string } }>(
      this.pathResetPassword(),
      body
    );
  }

  private pathVerificarIdentidad(): string {
    return this.flowContext.isCcsw5
      ? `${this.base}/ccsw5/verificar-identidad`
      : `${this.base}/verificar-identidad`;
  }

  private pathInformacionUsuario(): string {
    return this.flowContext.isCcsw5
      ? `${this.base}/ccsw5/informacion-usuario`
      : `${this.base}/informacion-usuario`;
  }

  private pathResetPassword(): string {
    return this.flowContext.isCcsw5
      ? `${this.base}/ccsw5/reset-password`
      : `${this.base}/reset-password`;
  }

  private get storagePrefix(): string {
    return this.flowContext.isCcsw5 ? STORAGE_PREFIX_CCSW5 : STORAGE_PREFIX_TV;
  }

  /**
   * Cifra el body JSON y envía `{ data }`. Si cipher no está configurado, envía en claro
   * (solo útil en entornos de prueba sin cifrado).
   */
  private postEncrypted<T>(url: string, body: unknown): Observable<T> {
    if (!this.cipher.isEncryptionEnabled) {
      return this.http.post<T>(url, body, this.jsonHeaders());
    }

    return from(this.cipher.encrypt(JSON.stringify(body))).pipe(
      switchMap((encrypted) =>
        this.http.post<{ data?: string } | T>(url, { data: encrypted }, this.jsonHeaders())
      ),
      switchMap((response) => this.unwrapCipherResponse<T>(response))
    );
  }

  private unwrapCipherResponse<T>(response: { data?: string } | T): Observable<T> {
    const wrapped = response as { data?: unknown };
    if (wrapped?.data && typeof wrapped.data === 'string') {
      return from(this.cipher.decrypt(wrapped.data)).pipe(
        map((plain) => JSON.parse(plain) as T)
      );
    }
    return of(response as T);
  }

  private jsonHeaders(): { headers: HttpHeaders } {
    return { headers: new HttpHeaders({ 'Content-Type': 'application/json' }) };
  }

  getStorage(key: string): string | null {
    return localStorage.getItem(this.storagePrefix + key);
  }

  setStorage(key: string, value: string): void {
    localStorage.setItem(this.storagePrefix + key, value);
  }

  clearFlowStorage(): void {
    ['datosVerificacion', 'transactionId', 'ordenPedidoId', 'solicitudTitularId'].forEach(k =>
      localStorage.removeItem(this.storagePrefix + k)
    );
  }
}
